
import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import { withFormUtils } from "../../../hoc/withFormUtils";
import { withRouter } from 'react-router-dom';
import { Button } from "reactstrap";
import {
    getPaymentToOrderStatusRules,
    getPaymentToOrderStatusRule,
    addEditPaymentToOrderStatusRule,
    deletePaymentToOrderStatusRule,
    getOrderStatuses
} from "../../../redux/methods/extrasSettings";
import useFetch from "../../../hooks/useFetch";
import PropTypes from "prop-types";
import TableGenerator from "../../generators/TableGenerator";
import PaymentToOrderStatusRuleEditionModal from "../../modals/PaymentToOrderStatusRuleEditionModal";
import { generateTranslatedOrderStatuses } from "../../../utils/orderStatuses";

const propTypes =
{
    forceRefetch: PropTypes.bool,
    getPaymentToOrderStatusRules: PropTypes.func,
    getPaymentToOrderStatusRule: PropTypes.func,
    addEditPaymentToOrderStatusRule: PropTypes.func,
    deletePaymentToOrderStatusRule: PropTypes.func,
    getOrderStatuses: PropTypes.func,
    utils: PropTypes.object
};

const OrderStatusToPaymentRuleTable =
(
    props
) =>
{
    const {
        forceRefetch,
        getPaymentToOrderStatusRules,
        getPaymentToOrderStatusRule,
        addEditPaymentToOrderStatusRule,
        deletePaymentToOrderStatusRule,
        getOrderStatuses,
        utils
    } = props;

    const translations = {
        title: 'Reguly mapowania statusow platnosci',
        headers: {
            name: 'Nazwa',
            previousPaymentStatus: 'Poprzedni status platnosci',
            paymentStatus: 'Status platnosci',
            currentOrderStatus: 'Biezacy status zamowienia',
            targetOrderStatus: 'Docelowy status zamowienia',
            priority: 'Priorytet',
            enabled: 'Aktywna',
            actions: 'Akcje'
        },
        tooltips: {
            add: 'Dodaj regule',
            edit: 'Edytuj regule',
            remove: 'Usun regule'
        },
        buttons: {
            create: 'DODAJ'
        },
        deleteModal: {
            header: 'Usun regule',
            body: 'Czy na pewno chcesz usunac wybrana regule?',
            confirm: 'Usun',
            cancel: 'Wroc'
        },
        editModal: {
            title: 'Edycja reguly',
            labels: {
                name: 'Nazwa',
                previousPaymentStatus: 'Poprzedni status platnosci',
                paymentStatus: 'Status platnosci',
                currentOrderStatus: 'Biezacy status zamowienia',
                orderStatus: 'Docelowy status zamowienia',
                priority: 'Priorytet',
                enabled: 'Aktywna'
            },
            buttons: {
                confirm: 'Zapisz',
                cancel: 'Anuluj'
            }
        },
        errors: {
            paymentStatusRequired: 'Wybierz status platnosci.',
            orderStatusRequired: 'Wybierz docelowy status zamowienia.'
        }
    };

    const [ isEditModalOpen, setIsEditModalOpen ] = useState(false);
    const [ selectedRule, setSelectedRule ] = useState(null);

    const [items] = useFetch(getPaymentToOrderStatusRules, null, [], forceRefetch);
    const [allOrderStatuses] = useFetch(getOrderStatuses);

    const tableItems = useMemo(() => (items || []).map(item => ({
        ...item,
        enabledLabel: item.enabled ? 'Tak' : 'Nie'
    })), [items]);

    const paymentStatusOptions = useMemo(() => {
        if (!items) {
            return [];
        }

        const unique = new Map();

        items.forEach(item => {
            if (!unique.has(item.paymentStatus)) {
                unique.set(item.paymentStatus, {
                    id: item.paymentStatus,
                    label: item.paymentStatusName
                });
            }

            // eslint-disable-next-line no-undefined
            if (item.previousPaymentStatus !== null && item.previousPaymentStatus !== undefined && !unique.has(item.previousPaymentStatus)) {
                unique.set(item.previousPaymentStatus, {
                    id: item.previousPaymentStatus,
                    label: item.previousPaymentStatusName
                });
            }
        });

        return [...unique.values()];
    }, [items]);

    const translatedOrderStatuses = useMemo(() => {
        if (!allOrderStatuses) {
            return [];
        }

        return generateTranslatedOrderStatuses(allOrderStatuses, utils.currentLanguage());
    }, [allOrderStatuses, utils]);

    const orderStatusOptions = useMemo(() => {
        if (!translatedOrderStatuses.length) {
            return [];
        }

        return translatedOrderStatuses.map(status => ({
            id: status.id,
            label: status.translatedName || status.name || String(status.id)
        }));
    }, [translatedOrderStatuses]);

    const previousPaymentStatusOptions = useMemo(() => [
        { id: null, label: '*' },
        ...paymentStatusOptions
    ], [paymentStatusOptions]);

    const currentOrderStatusOptions = useMemo(() => [
        { id: null, label: '*' },
        ...orderStatusOptions
    ], [orderStatusOptions]);

    const openEditModal = (rule) => {
        setSelectedRule(rule);
        setIsEditModalOpen(true);
    };

    const addAction = () => {
        openEditModal({
            id: null,
            name: '',
            previousPaymentStatus: null,
            paymentStatus: null,
            currentOrderStatusId: null,
            orderStatusId: null,
            priority: 0,
            enabled: true
        });
    };

    const extractRuleFromPayload = payload => {
        if (!payload) {
            return null;
        }

        if (payload.rule) {
            return payload.rule;
        }

        if (payload.paymentToOrderStatusRule) {
            return payload.paymentToOrderStatusRule;
        }

        return payload;
    };

    const editAction = (id, rowItem) => async () => {
        const foundRule = rowItem || items?.find(ruleItem => Number(ruleItem.id) === Number(id));

        if (foundRule) {
            // Open immediately with row data to avoid blank form state.
            openEditModal(foundRule);
        }

        const item = await getPaymentToOrderStatusRule(id);

        if (item && item.payload && !item.payload.errors && !item.error)
        {
            const detailedRule = extractRuleFromPayload(item.payload);

            if (detailedRule) {
                openEditModal({
                    ...(foundRule || {}),
                    ...detailedRule
                });
            }
        }
    };

    const deleteAction = id => () => {
        deletePaymentToOrderStatusRule(id);
    };

    const handleEditAction = form =>
    {
        // eslint-disable-next-line no-undefined
        if (form.paymentStatus === null || form.paymentStatus === undefined)
        {
            utils.error(translations.errors.paymentStatusRequired);
            return;
        }

        // eslint-disable-next-line no-undefined
        if (form.orderStatusId === null || form.orderStatusId === undefined)
        {
            utils.error(translations.errors.orderStatusRequired);
            return;
        }

        addEditPaymentToOrderStatusRule({
            id: form.id ?? null,
            name: form.name,
            previousPaymentStatus: form.previousPaymentStatus ?? null,
            paymentStatus: Number(form.paymentStatus),
            currentOrderStatusId: form.currentOrderStatusId ?? null,
            orderStatusId: Number(form.orderStatusId),
            priority: Number(form.priority),
            enabled: !!form.enabled
        });

        setIsEditModalOpen(false);
    };

    return items ? (
        <>
            <TableGenerator
                items={tableItems}
                expectedActions={['edit', 'delete']}
                actions={{ editAction, deleteAction }}
                defaultAction={editAction}
                translations={translations}
                customIdSelector='id'
                highlightOnSelectStyle={null}
                performPageSizeSelect={null}
                pageSizeOptions={[]}
                detailsComponent={null}
                columns={[
                    {
                        header: translations.headers.name,
                        value: 'name'
                    },
                    {
                        header: translations.headers.previousPaymentStatus,
                        value: 'previousPaymentStatusName'
                    },
                    {
                        header: translations.headers.paymentStatus,
                        value: 'paymentStatusName'
                    },
                    {
                        header: translations.headers.currentOrderStatus,
                        value: 'currentOrderStatus'
                    },
                    {
                        header: translations.headers.targetOrderStatus,
                        value: 'orderStatus'
                    },
                    {
                        header: translations.headers.priority,
                        value: 'priority'
                    },
                    {
                        header: translations.headers.enabled,
                        value: 'enabledLabel'
                    },
                    {
                        header: translations.headers.actions,
                        type: 'actions'
                    }
                ]}
            />
            <aside className='text-right mt-3'>
                <Button color='primary' onClick={addAction}>
                    <i className='fa fa-plus mr-2' />
                    <span>{translations.buttons.create}</span>
                </Button>
            </aside>
            <PaymentToOrderStatusRuleEditionModal
                isOpen={isEditModalOpen}
                setIsOpen={setIsEditModalOpen}
                onSubmit={handleEditAction}
                translations={translations.editModal}
                rule={selectedRule}
                previousPaymentStatusOptions={previousPaymentStatusOptions}
                paymentStatusOptions={paymentStatusOptions}
                currentOrderStatusOptions={currentOrderStatusOptions}
                orderStatusOptions={orderStatusOptions}
            />
        </>
    ) : null;
};

OrderStatusToPaymentRuleTable.propTypes = propTypes;

const mapStateToProps = (state) => ({
    forceRefetch: state.refetchers.orderStatuses
});

const mapDispatch = {
    getPaymentToOrderStatusRules,
    getPaymentToOrderStatusRule,
    addEditPaymentToOrderStatusRule,
    deletePaymentToOrderStatusRule,
    getOrderStatuses
};

export default withFormUtils(withRouter(connect(mapStateToProps, mapDispatch)(OrderStatusToPaymentRuleTable)));